import React, { useCallback, useMemo, useState } from 'react';

import AddIcon from '@mui/icons-material/Add';
import { Box, Button, Divider, MenuItem, Select, Stack, Typography } from '@mui/material';

import { CatalogRoom, ConflictResolution, ManualRoom } from '~/types/stopsell';

import { sliceResolutionsByRoom } from '~/utils/stopsellConflicts';

import StopsellManualRoomCard from './StopsellManualRoomCard';

interface Props {
  catalogRooms: Array<CatalogRoom>;
  extractedRoomTypeIds: Set<string>;
  manualRooms: Array<ManualRoom>;
  manualResolutions: Record<string, ConflictResolution>;
  onAddRoom: (catalogRoomId: string, catalogRoomName: string) => void;
  onRemoveRoom: (roomIndex: number) => void;
  onAddDate: (roomIndex: number, start: string, end: string) => void;
  onRemoveDate: (roomIndex: number, dateIndex: number) => void;
  onToggleDate: (roomIndex: number, dateIndex: number, selected: boolean) => void;
  onResolve: (roomIndex: number, dateIndex: number, rateId: string, status: ConflictResolution) => void;
  onUndo: (roomIndex: number, dateIndex: number, rateId: string) => void;
}

export default function StopsellManualRoomsSection({
  catalogRooms,
  extractedRoomTypeIds,
  manualRooms,
  manualResolutions,
  onAddRoom,
  onRemoveRoom,
  onAddDate,
  onRemoveDate,
  onToggleDate,
  onResolve,
  onUndo,
}: Props) {
  const [selectedRoomId, setSelectedRoomId] = useState('');

  const availableRooms = useMemo(
    () =>
      catalogRooms.filter(
        (room) => !extractedRoomTypeIds.has(room.id) && !manualRooms.some((m) => m.catalogRoomId === room.id),
      ),
    [catalogRooms, extractedRoomTypeIds, manualRooms],
  );

  const handleAddRoom = useCallback(() => {
    const room = availableRooms.find((r) => r.id === selectedRoomId);
    if (!room) return;
    onAddRoom(room.id, room.name);
    setSelectedRoomId('');
  }, [availableRooms, selectedRoomId, onAddRoom]);

  return (
    <Box>
      <Divider sx={{ borderStyle: 'dashed', my: 2 }} />
      <Stack direction="row" alignItems="center" gap={1} mb={2}>
        <AddIcon fontSize="small" />
        <Typography fontWeight={500}>Add Missing Rooms</Typography>
      </Stack>

      <Stack direction="row" gap={1} mb={2}>
        <Select
          size="small"
          displayEmpty
          value={selectedRoomId}
          onChange={(e) => setSelectedRoomId(e.target.value)}
          sx={{ minWidth: 240 }}
        >
          <MenuItem value="" disabled>
            Select a room...
          </MenuItem>
          {availableRooms.map((room) => (
            <MenuItem key={room.id} value={room.id}>
              {room.name}
            </MenuItem>
          ))}
        </Select>
        <Button variant="contained" disabled={!selectedRoomId} onClick={handleAddRoom}>
          Add Room
        </Button>
      </Stack>

      <Stack gap={2}>
        {manualRooms.map((room, roomIndex) => {
          const catalogRoom = catalogRooms.find((c) => c.id === room.catalogRoomId);
          const rateResolutions = sliceResolutionsByRoom(manualResolutions, roomIndex);
          return (
            <StopsellManualRoomCard
              key={room.catalogRoomId}
              room={room}
              roomIndex={roomIndex}
              blackoutDates={catalogRoom?.blackoutDates ?? []}
              catalogRoomRates={catalogRoom?.roomRates ?? []}
              rateResolutions={rateResolutions}
              onAddDate={onAddDate}
              onRemoveRoom={onRemoveRoom}
              onRemoveDate={onRemoveDate}
              onToggleDate={onToggleDate}
              onResolve={onResolve}
              onUndo={onUndo}
            />
          );
        })}
      </Stack>
    </Box>
  );
}
